/*
 * NotFound (404) page
 *
 * Displayed when a user navigates to a route that doesn't exist.
 */

import React from 'react';
import { Box, Typography, Button, Container, Stack } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ReactComponent as LogoSVG } from '../components/LogoNoText.svg';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

function NotFound() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#0a0a0a',
        color: '#fff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Helmet>
        <title>404 - Page Not Found | Kingdom MMA and Fitness</title>
        <meta name="description" content="The page you're looking for doesn't exist. Return to Kingdom MMA and Fitness homepage." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Subtle background pattern */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          opacity: 0.03,
          backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, #fff 35px, #fff 36px)',
          pointerEvents: 'none',
        }}
      />

      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Stack spacing={4} alignItems="center" textAlign="center">
            {/* Logo */}
            <LogoSVG
              alt="Kingdom MMA and Fitness logo"
              style={{
                width: 'clamp(120px, 30vw, 200px)',
                height: 'auto',
                filter: 'drop-shadow(0 0 8px rgba(255,255,255,0.3))',
                cursor: 'pointer',
              }}
              onClick={() => navigate('/')}
            />

            {/* 404 Number */}
            <Typography
              variant="h1"
              sx={{
                fontSize: 'clamp(5rem, 15vw, 12rem)',
                fontWeight: 900,
                color: '#fff',
                lineHeight: 0.9,
                textShadow: '0 4px 20px rgba(255,255,255,0.2)',
                letterSpacing: '-0.02em',
              }}
            >
              404
            </Typography>

            {/* Message */}
            <Box sx={{ maxWidth: '600px' }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: 'clamp(1.5rem, 3vw, 2rem)',
                  fontWeight: 600,
                  mb: 2,
                  color: '#fff',
                }}
              >
                Page Not Found
              </Typography>

              <Typography
                variant="body1"
                sx={{
                  fontSize: 'clamp(1rem, 1.5vw, 1.1rem)',
                  color: '#bbb',
                  lineHeight: 1.6,
                  mb: 4,
                }}
              >
                The page you're looking for doesn't exist or has been moved.
              </Typography>
            </Box>

            {/* CTA Buttons */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/')}
                sx={{
                  background: 'linear-gradient(135deg, #ffffff 0%, #888888 100%)',
                  color: '#121212',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(255,255,255,0.2)',
                  minWidth: { xs: '100%', sm: '180px' },
                  '&:hover': {
                    background: 'linear-gradient(135deg, #888888 0%, #ffffff 100%)',
                    color: '#fff',
                    boxShadow: '0 8px 32px rgba(255,255,255,0.3)',
                  },
                }}
              >
                Return Home
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: '#888',
                  color: '#ccc',
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  px: 4,
                  py: 1.5,
                  borderRadius: 3,
                  minWidth: { xs: '100%', sm: '180px' },
                  '&:hover': {
                    borderColor: '#fff',
                    color: '#fff',
                    backgroundColor: 'rgba(255,255,255,0.05)',
                  },
                }}
              >
                Go Back
              </Button>
            </Stack>

            {/* Quick Links */}
            <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
              <Typography
                variant="body2"
                sx={{ color: '#888', mb: 2, fontSize: '0.9rem' }}
              >
                Looking for something specific?
              </Typography>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={{ xs: 1, sm: 3 }}
                justifyContent="center"
                sx={{ flexWrap: 'wrap' }}
              >
                {[
                  { label: 'Schedule', path: '/schedule' },
                  { label: 'About Us', path: '/about' },
                  { label: 'Memberships', path: '/memberships' },
                  { label: 'Contact', path: '/contact-us' },
                ].map((link) => (
                  <Button
                    key={link.path}
                    onClick={() => navigate(link.path)}
                    sx={{
                      color: '#aaa',
                      fontSize: '0.9rem',
                      textTransform: 'none',
                      minWidth: 0,
                      p: 1,
                      '&:hover': {
                        color: '#fff',
                        backgroundColor: 'transparent',
                        textDecoration: 'underline',
                      },
                    }}
                  >
                    {link.label}
                  </Button>
                ))}
              </Stack>
            </Box>
          </Stack>
        </motion.div>
      </Container>
    </Box>
  );
}

export default NotFound;
